import React, { useEffect, useState } from 'react';

const PaymentSuccess = ({ onContinue }) => {
  const [verifying, setVerifying] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      setError('Keine Zahlungssession gefunden');
      setVerifying(false);
    }
  }, []);

  const verifyPayment = async (sessionId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://centeo-backend.onrender.com';
      console.log('🔍 Verifying payment with session ID:', sessionId);
      console.log('🌐 Using API URL:', apiUrl);
      
      const response = await fetch(`${apiUrl}/api/payment/verify-payment/${sessionId}`);
      console.log('📡 Verification response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📋 Verification data:', data);
      
      if (data.success && data.paid) {
        setPaymentData(data);
      } else {
        setError('Zahlung konnte nicht verifiziert werden');
      }
    } catch (err) {
      setError('Fehler bei der Zahlungsverifikation');
    } finally {
      setVerifying(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900">Zahlung wird verifiziert...</h2>
          <p className="text-gray-600">Bitte warten Sie einen Moment.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-red-600 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Fehler bei der Zahlung</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => window.location.href = '/'}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold"
          >
            Zurück zur Startseite
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white flex items-center justify-center px-4">
      <div className="text-center max-w-2xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Success Animation */}
        <div className="text-green-600 text-6xl sm:text-8xl mb-4 sm:mb-6 transform transition-all duration-1000 ease-out animate-fade-in-scale">✅</div>
        
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
          Zahlung erfolgreich!
        </h1>
        
        <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8">
          Vielen Dank für Ihren Kauf. Ihre Förderanalyse wird jetzt erstellt.
        </p>

        {/* Payment Details */}
        <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-200 mb-6 sm:mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📧 Bestätigung</h3>
          <div className="space-y-2 text-left">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
              <span className="text-gray-600">E-Mail:</span>
              <span className="font-medium break-all">{paymentData?.customerEmail}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Betrag:</span>
              <span className="font-medium">9,90€</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Service:</span>
              <span className="font-medium">Centeo Förderanalyse</span>
            </div>
          </div>
        </div>

        {/* What happens next */}
        <div className="bg-blue-50 rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🚀 Was passiert als nächstes?</h3>
          <div className="space-y-3 text-left">
            <div className="flex items-start gap-3">
              <span className="text-blue-600 font-bold">1.</span>
              <span className="text-gray-700">Ihre Daten werden analysiert</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-blue-600 font-bold">2.</span>
              <span className="text-gray-700">Passende Förderprogramme werden ermittelt</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-blue-600 font-bold">3.</span>
              <span className="text-gray-700">Ergebnisse werden an Ihre E-Mail gesendet</span>
            </div>
          </div>
        </div>

        {/* Continue Button */}
        <button 
          onClick={() => onContinue(paymentData)}
          className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-base sm:text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 touch-manipulation"
        >
          Zu den Ergebnissen →
        </button>

        <div className="mt-6 text-sm text-gray-500">
          Bei Fragen kontaktieren Sie uns: <a href="mailto:ciao@centeo.one" className="text-green-600 hover:underline">ciao@centeo.one</a>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;